import React, { useState, useEffect } from "react";
import { FaTimesCircle, FaChevronLeft, FaChevronRight, FaSearch, FaCopy } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import DescriptionViewer from '../../Common/DescriptionViewer';

const RejectedAdvertisements = () => {
  const navigate = useNavigate();
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // State for Searching by ID
  const rowsPerPage = 50;

  useEffect(() => {
    const fetchRejectedAds = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/advertiser/advertisements?status=rejected`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = response.data.data || response.data;
        const rejectedAds = Array.isArray(data)
          ? data.filter((ad) => ad.status === "rejected")
          : [];

        setAdvertisements(rejectedAds);
        setRejectedCount(rejectedAds.length);
      } catch (error) {
        toast.error("Failed to load rejected advertisements");
      } finally {
        setLoading(false);
      }
    };
    fetchRejectedAds();
  }, []);

  // Search & Pagination logic
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredAds = advertisements.filter(ad => {
    if (!searchTerm) return true;
    const searchStr = searchTerm.toLowerCase();
    return ad._id?.toLowerCase().includes(searchStr) ||
      ad.adId?.toLowerCase().includes(searchStr);
  });

  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const paginatedAds = [...filteredAds].reverse().slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex flex-col mb-6 gap-4 mt-2">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
          Rejected Advertisements
        </h1>
        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30' },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30' },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30' },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30' },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30' },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30', active: true }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4 sm:mb-8">
        <div className="bg-rose-500 p-3 sm:p-6 rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">
                Rejected Advertisements
              </p>
              <p className="text-2xl font-bold mt-1">{rejectedCount}</p>
            </div>
            <FaTimesCircle className="w-8 h-8 sm:w-10 sm:h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-rose-500"></div>
        </div>
      ) : advertisements.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaTimesCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No Rejected Advertisements
          </h3>
          <p className="text-gray-500">
            You don't have any rejected advertisements.
          </p>
        </div>
      ) : (
        <>
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Search by Advertisement ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            </div>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm border border-slate-200">
              <thead>
                <tr className="bg-rose-50 text-rose-800 border-b border-rose-100">
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Media</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Ad ID</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Description</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Rejection Reason</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Budget</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Ad Type</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Target User</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Channels</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Location</th>
                  <th className="py-2.5 px-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedAds.map((ad) => (
                  <tr key={ad.id || ad._id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 border-r border-slate-100">
                      {ad.mediaType === "video" ? (
                        <video
                          src={ad.videoUrl}
                          className="w-16 h-12 object-cover rounded-md shadow-sm border border-slate-200"
                        />
                      ) : ad.imageUrl ? (
                        <img
                          src={ad.imageUrl}
                          alt={ad.title || "Ad Media"}
                          className="w-16 h-12 object-cover rounded-md shadow-sm border border-slate-200"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-slate-100 rounded-md border border-slate-200 flex items-center justify-center">
                          <span className="text-[10px] text-slate-400 font-bold uppercase">N/A</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-md w-max group/id">
                        <span className="font-semibold text-[10px] sm:text-xs md:text-sm truncate max-w-[80px] sm:max-w-[120px] md:max-w-none text-slate-700">{ad.id || ad._id}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(ad.id || ad._id);
                            toast.success('ID Copied!');
                          }}
                          className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5"
                          title="Copy ID"
                        >
                          <FaCopy className="w-3 h-3 group-hover/id:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100 min-w-[150px]">
                      <DescriptionViewer description={ad.mediaDescription || "N/A"} wordLimit={5} textSize="text-xs text-slate-600" />
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100 min-w-[150px]">
                      <div className="bg-rose-50 border border-rose-100 text-rose-700 px-2 py-1.5 rounded-md text-xs font-medium">
                        <DescriptionViewer description={ad.adminRejectNote || "N/A"} wordLimit={5} textSize="text-xs text-rose-700" />
                      </div>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded text-[10px] font-bold">
                        ₹{ad.totalCost || "N/A"}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <span className="bg-slate-100 text-slate-700 border border-slate-200 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider">
                        {ad.adType || "N/A"}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider ${ad.userType === 'influencer'
                        ? 'bg-purple-50 text-purple-600 border border-purple-100'
                        : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                        }`}>
                        {ad.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100 max-w-[120px] truncate">
                      <span className="text-[10px] font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                        {Array.isArray(ad.channelType) ? ad.channelType.join(", ") : "N/A"}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <div className="text-[10px] font-bold text-slate-800">
                        {ad.pfState || "Not specified"}
                      </div>
                      <div className="text-[9px] text-slate-500 max-w-[100px] truncate">
                        {ad.pfCities && ad.pfCities.length > 0 ? ad.pfCities.join(", ") : "No cities"}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-[10px] font-mono text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-100 font-bold">
                        {ad.startDate || "N/A"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Layout */}
          <div className="lg:hidden flex flex-col gap-3">
            {paginatedAds.map((ad) => (
              <div key={ad.id || ad._id} className="bg-white rounded-xl shadow-sm border border-rose-100 p-3 flex flex-col gap-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-2 h-full bg-rose-400"></div>

                <div className="flex justify-between items-start border-b border-slate-100 pb-3 pr-2">
                  <div className="flex gap-3 items-center">
                    {ad.mediaType === "video" ? (
                      <video src={ad.videoUrl} className="w-14 h-14 object-cover rounded-lg shadow-sm border border-slate-200" />
                    ) : ad.imageUrl ? (
                      <img src={ad.imageUrl} alt={ad.title || "Ad Media"} className="w-14 h-14 object-cover rounded-lg shadow-sm border border-slate-200" />
                    ) : (
                      <div className="w-14 h-14 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
                        <span className="text-[10px] text-slate-400 font-bold uppercase">N/A</span>
                      </div>
                    )}

                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 bg-slate-50 border border-slate-100 px-1 py-0.5 rounded w-max group/id">
                        <span className="text-[9px] font-black text-slate-500 font-mono tracking-tighter uppercase">#{(ad.id || ad._id).substring((ad.id || ad._id).length - 8)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(ad.id || ad._id);
                            toast.success('ID Copied!');
                          }}
                          className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5"
                          title="Copy ID"
                        >
                          <FaCopy className="w-2.5 h-2.5 group-hover/id:scale-110 transition-transform" />
                        </button>
                      </div>
                      <span className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${ad.userType === 'influencer' ? 'bg-purple-50 text-purple-600 border border-purple-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}>
                        {ad.userType === 'influencer' ? 'Influencer' : 'Reporter'}
                      </span>
                      <span className="text-[10px] font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded uppercase w-fit">{ad.adType || "N/A"}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded text-xs font-black">
                      ₹{ad.totalCost || "N/A"}
                    </span>
                    <span className="text-[9px] font-mono text-slate-500">{ad.startDate || "N/A"}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pr-2">
                  <div className="bg-rose-50 border border-rose-100 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-600">
                      <FaTimesCircle className="w-3 h-3" />
                      <span className="text-[10px] font-black uppercase tracking-wider">Reason for Rejection</span>
                    </div>
                    <DescriptionViewer description={ad.adminRejectNote || "N/A"} wordLimit={8} textSize="text-[10px] sm:text-xs text-rose-800" />
                  </div>

                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5 block">Description</span>
                    <DescriptionViewer description={ad.mediaDescription || "N/A"} wordLimit={6} textSize="text-xs text-slate-600" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 pr-2">
                  <div className="flex flex-col flex-1 min-w-[120px]">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Channels</span>
                    <span className="text-[10px] font-bold text-slate-700 truncate">{Array.isArray(ad.channelType) ? ad.channelType.join(", ") : "N/A"}</span>
                  </div>
                  <div className="flex flex-col flex-1 min-w-[120px]">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Location</span>
                    <span className="text-[10px] font-bold text-slate-700">{ad.pfState || "N/A"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Show empty state if nothing matches search but list is not entirely empty overall */}
          {advertisements.length > 0 && filteredAds.length === 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center text-slate-500 mt-4">
              No advertisements found matching "{searchTerm}"
            </div>
          )}

        </>
      )}
      {/* Pagination Controls */}
      {filteredAds.length > 0 && totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>
            {/* Page number buttons */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === page ? 'bg-yellow-300 font-bold text-yellow-900' : ''}`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default RejectedAdvertisements;
